import { CheckOutlined } from '@ant-design/icons';
import { Button, Checkbox, Modal, Spin } from 'antd';
import { CheckboxValueType } from 'antd/lib/checkbox/Group';
import { RadioChangeEvent } from 'antd/lib/radio';
import React, { Fragment, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  MethodWay,
  openEULAPdf,
  openPrivatePolicy,
} from '../../../utils/common';
import styles from '../../../pages/Checkout/Checkout.module.scss';
import useAuthStore from "../../../store/authStore";
import useCartStore from "../../../store/cartStore";
import useShoppingCart from "./hooks/useShoppingCart";
import usePriceSummary from "./hooks/usePriceSummary";
import useBillingAddress from "./hooks/useBillingAddress";
import useOrderCreation from "./hooks/useOrderCreation";
import useCheckoutEligibility from "./hooks/useCheckoutEligibility";
import useStoreReset from "./hooks/useStoreReset";
import usePaymentRedirection from "./hooks/usePaymentRedirection";

const downloadOptions = [
  { label: '.csv', value: 'csv' },
  { label: '.mat', value: 'mat' },
  { label: '.nc', value: 'nc' },
  { label: '.dfs0', value: 'dfs0' },
];

export default function Summary() {
  const shouldShowCompanyWarning = useAuthStore(state => state.shouldShowCompanyWarning);
  const paymentMethod = useCartStore(state => state.paymentMethod);

  const [checkPolicy, setCheckPolicy] = useState<boolean>(false);
  const [showLocation, setShowLocation] = useState<boolean>(true);
  const [showWarn, setShowWarn] = useState<boolean>(false);
  const [downloadValues, setDownloadValues] = useState<CheckboxValueType[]>(['csv']);

  const { billingAddress } = useBillingAddress();
  const { shoppingCartWithSummary, refetchAndUpdateItems } = useShoppingCart();
  const { priceSummary, emptyPrice, loadingPrice, currency } = usePriceSummary();
  const { createOrder, creatingOrder, createOrderError, resetOrderError,
          paymentUrl, resetPaymentUrl, isOrderSuccessfulModalOpen, resetSuccessModal } = useOrderCreation();
  const { checkoutDisabled } = useCheckoutEligibility({ checkPolicy, emptyPrice });
  const { clearStore } = useStoreReset();
  const { redirectingToPaymentProvider, handlePaymentRedirection } = usePaymentRedirection();

  function onPolicyRadioChange(e: RadioChangeEvent) {
    setCheckPolicy(e.target.checked);
    setShowWarn(false);
  }

  function onLocationRadioChange(e: RadioChangeEvent) {
    setShowLocation(e.target.checked);
  }
  async function onClickCheckout() {
    if (!checkPolicy) {
      setShowWarn(true);
    } else {
      await handleCreateOrder();
    }
  }

  async function handleCreateOrder() {
    if (!billingAddress && !shouldShowCompanyWarning()) {
      resetOrderError();
      return;
    }

    await createOrder({
      shoppingCartWithSummary,
      downloadValues,
      billingAddress,
      showLocation
    });
  }

  function onCloseCreateOrderFailedDialog() {
    resetOrderError();
  }

  function onCloseGoToPaymentDialog() {
    resetPaymentUrl();
  }

  async function onCloseFreeCheckoutSucceededDialog() {
    resetSuccessModal();
    clearStore();
    await refetchAndUpdateItems();
  }

  function onChangeDownloadOptions(checkedList: CheckboxValueType[]) {
    setDownloadValues(checkedList);
  }
  function onRedirectToPaymentGateway(e: React.MouseEvent) {
    e.preventDefault();
    handlePaymentRedirection(paymentUrl, () => {
      resetOrderError();
    });
  }

  return (
    <div className={styles.summaryRoot}>
      <Modal
        closable={false}
        keyboard={false}
        open={isOrderSuccessfulModalOpen}
        title={<span id="Checkout-Success_Popup">Checkout successful</span>}
        footer={
          <Link to={'/'}>
            <Button id="Checkout-Success_Ok_Button" onClick={onCloseFreeCheckoutSucceededDialog}>OK</Button>
          </Link>
        }
      >
          An email with a link to download the data will be sent to you within a few minutes. You will be redirected to the main page.
      </Modal>
      <Modal
        open={createOrderError !== ''}
        title={'Error during order processing'}
        footer={<Button onClick={onCloseCreateOrderFailedDialog}>OK</Button>}
      >
        <p>{createOrderError}</p>
      </Modal>
      <Modal
        onCancel={onCloseGoToPaymentDialog}
        closable={!redirectingToPaymentProvider}
        keyboard={!redirectingToPaymentProvider}
        open={paymentUrl !== ''}
        title={'Your order has been created'}
        footer={null}
      >
        <div className={styles.goToPaymentContainer}>
          <div className={styles.space} />
          <Button
            onClick={onRedirectToPaymentGateway}
            id="Checkout-Payment_Button"
            type={'link'}
          >
            <div className={styles.goToPaymentStyle}>
              Go to payment provider
            </div>
          </Button>
          <div className={styles.space} />
          {redirectingToPaymentProvider && (
            <div className={styles.spinner}>
              <Spin size="large" />
              <div className={styles.space} />
            </div>
          )}
        </div>
      </Modal>
      {loadingPrice ? (
        <div className={styles.spinner}>
          <Spin size="large" />
        </div>
      ) : (
        <div>
          <div className={styles.summaryContent}>
            <p className={styles.summaryInfo}>
              <span>
                Price: {emptyPrice ? '' : currency + priceSummary.Price}
              </span>
            </p>
            <p className={styles.summaryInfo}>
              <span>
                Multipoint-discount:
                {emptyPrice ? '' : currency + priceSummary.MultipointDiscount}
              </span>
            </p>
            <p className={styles.summaryInfo}>
              <span>
                Credit Deducted:
                {emptyPrice ? '' : currency + priceSummary.CreditDeducted}
              </span>
            </p>
            {paymentMethod === MethodWay.INV && (
              <p className={styles.summaryInfo}>
                <span>Invoice Fee</span>
              </p>
            )}
          </div>
          <div className={styles.summaryContent}>
            <p className={styles.summaryInfo}>
              <span>
                Subtotal: {emptyPrice ? '' : currency + priceSummary.Subtotal}
              </span>
            </p>
            <p className={styles.summaryInfo}>
              <span>
                Estimated Tax
                {emptyPrice
                  ? ':'
                  : ' (VAT): ' + currency + priceSummary.EstimatedVAT}
              </span>
            </p>
          </div>
          <div className={styles.summaryContent} style={{ border: 'none' }}>
            <p className={styles.summaryInfo}>
              <span>
                Total: {emptyPrice ? '' : currency + priceSummary.Total}
              </span>
            </p>
          </div>

          <div className={styles.outputType}>
            <p>Select file format(s) of time series data:</p>
            <Checkbox.Group
              options={downloadOptions}
              defaultValue={downloadValues}
              onChange={onChangeDownloadOptions}
            />
          </div>
        </div>
      )}

      <div>
        <div>
          <Link to={'/app'}>
            <Button type="primary" className={styles.checkoutButton} id="Checkout-Back_Button">
              Go back to main page
            </Button>
          </Link>

          <Button
            id="Checkout-Checkout_Button"
            loading={creatingOrder}
            disabled={checkoutDisabled}
            type="primary"
            icon={<CheckOutlined />}
            className={
              styles.checkoutButton +
              ` ${checkoutDisabled && styles.checkoutButtonDisabled}`
            }
            onClick={onClickCheckout}
          >
            Checkout
          </Button>
        </div>

        <div>
          <p
            className={
              styles.summaryInfo + ` ${showWarn && styles.summaryWarn}`
            }
          >
            <Checkbox
              defaultChecked={checkPolicy}
              onChange={onPolicyRadioChange}
            >
              <span>
                I accept the
                <span
                  className={styles.summarySpecialText}
                  onClick={openEULAPdf}
                >
                  General EULA
                </span>
                and
                <span
                  className={styles.summarySpecialText}
                  onClick={openPrivatePolicy}
                >
                  Privacy Policy
                </span>
              </span>
              {showWarn && (
                <Fragment>
                  <br />
                  <span className={styles.summaryWarnText}>
                    Please tick checkbox before checkout.
                  </span>
                </Fragment>
              )}
            </Checkbox>
          </p>
          <p className={styles.summaryInfo}>
            <Checkbox
              defaultChecked={showLocation}
              onChange={onLocationRadioChange}
            >
              Show location coordinates on invoice
            </Checkbox>
          </p>
        </div>
      </div>
    </div>
  );
}
